import React from 'react';
// Framer Motion supprimé - utilisation d'animations CSS
import { X, Phone, Mail, Calendar, Heart, AlertCircle, User, FileText } from 'lucide-react';
import Avatar from '../common/Avatar';

const PatientDetailsModal = ({ patient, isOpen, onClose }) => {
  if (!patient) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Stable': return 'bg-green-100 text-green-800';
      case 'Critique': return 'bg-red-100 text-red-800';
      case 'En observation': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Stable': return <Heart className="h-3 w-3 mr-1" />;
      case 'Critique': return <AlertCircle className="h-3 w-3 mr-1" />;
      case 'En observation': return <User className="h-3 w-3 mr-1" />;
      default: return <User className="h-3 w-3 mr-1" />;
    }
  };

  return (
    <div>
      {isOpen && (
        <div
          
          
          
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <div
            
            
            
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <Avatar
                  src={patient.avatar}
                  alt={patient.name}
                  name={patient.name}
                  size="2xl"
                />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{patient.name}</h2>
                  <p className="text-gray-600">Patient ID: #{patient.id}</p>
                </div>
              </div>
              <button
                
                
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Informations personnelles */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Informations personnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{patient.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{patient.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{patient.age} ans / {patient.gender}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Heart className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Groupe sanguin: {patient.bloodType || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Informations médicales */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Informations médicales
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Statut:</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                      {getStatusIcon(patient.status)}
                      {patient.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Diagnostic:</span>
                    <span className="text-gray-900">{patient.diagnosis || 'En cours d\'évaluation'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Médecin assigné:</span>
                    <span className="text-gray-900">{patient.doctor || 'Dr. Non assigné'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Dernière visite:</span>
                    <span className="text-gray-900">{formatDate(patient.lastVisit)}</span>
                  </div>
                </div>
              </div>

              {/* Antécédents médicaux */}
              {(patient.medicalHistory || patient.allergies || patient.currentMedications) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Antécédents médicaux
                  </h3>
                  <div className="space-y-3">
                    {patient.medicalHistory && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Antécédents:</span>
                        <p className="text-gray-900 mt-1">{patient.medicalHistory}</p>
                      </div>
                    )}
                    {patient.allergies && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Allergies:</span>
                        <p className="text-gray-900 mt-1">{patient.allergies}</p>
                      </div>
                    )}
                    {patient.currentMedications && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Médicaments actuels:</span>
                        <p className="text-gray-900 mt-1">{patient.currentMedications}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact d'urgence */}
              {patient.emergencyContact && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Phone className="h-5 w-5 mr-2" />
                    Contact d'urgence
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Nom:</span>
                      <span className="text-gray-900">{patient.emergencyContact}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Téléphone:</span>
                      <span className="text-gray-900">{patient.emergencyPhone || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                
                
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Fermer
              </button>
              <button
                
                
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Modifier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetailsModal;
